import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isNull, isUndefined, isEqual, cloneDeep } from "lodash";

import locationService from "@plannel/services/data/location-service"
import workcenterService from "@plannel/services/data/workcenter-service";
import CalendarGroupRenderer from "@plannel/components/aggrid/CalendarGroupRenderer";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting, {gridPaste, nullSafeComparator } from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import DateUtils from "@plannel/utils/date-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const { snackBarMessageColor } = ColorUtil;
const { timeColumnDecimalLimit } = CodeUtil;

const WorkcenterMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);
  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [locationSearch, setLocationSearch] = useState(reduxViewState?.locationSearch ?? null);
  const [workcenterSearch, setWorkcenterSearch] = useState(reduxViewState?.workcenterSearch ?? null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [workcenters, setWorkcenters] = useState([]);

  const [locations, setLocations] = useState(reduxViewState?.locations ?? []);
  const locationMap = useRef({});

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const dropdownResourceValueFormatter = (data) => {
    if (typeof data?.value === "string") {
      return data.value;
    }

    return data.value ? data.value.name : "";
  }

  const valueFormatter = (param) => {
    if (param.column?.colDef) {
      const { colId } = param.column.getColDef();
      if (colId === "calendarGroup") {
        return param.value?.name ? param.value.name : t("notConfigured");
      }
    }

    return param.value?.name ? param.value.name : "";
  }

  const objectComparator = (value1, value2, property, isDescending) => {
    const val1 = !value1 ? "" : value1[property];
    const val2 = !value2 ? "" : value2[property];

    return nullSafeComparator(val1, val2, isDescending);
  }

  const checkInputValidation = (params) => {
    const colId = params.column.colId;

    if (isNaN(params.newValue) || isNull(params.newValue) || isUndefined(params.newValue)) {
      params.data[colId] = null;

      return true;
    }

    if (params.newValue < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InvalidValue") });
      return false;
    }

    const decimalPart = params.newValue.toString().split('.')[1];

    if (decimalPart && decimalPart.length > timeColumnDecimalLimit) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_LimitDecimalPoint", { N: timeColumnDecimalLimit }) });
      return false;
    }

    params.data[colId] = params.newValue;

    return true;
  }

  // 동결 구간은 버킷 개수이므로 0 이상의 정수만 허용한다. 미입력은 null(미설정)로 저장.
  const checkFrozenRangeValidation = (params) => {
    const { newValue } = params;

    if (isNull(newValue) || isUndefined(newValue) || newValue === "") {
      params.data.frozenRange = null;

      return true;
    }

    const frozenRange = Number(newValue);
    if (Number.isNaN(frozenRange) || !Number.isInteger(frozenRange) || frozenRange < 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntGreaterThanValue", { N: 0 }) });

      return false;
    }

    params.data.frozenRange = frozenRange;

    return true;
  }

  const onCellValueChanged = (params) => {
    gridPaste(params);

    const { column, node } = params;
    const colId = column.getColId();
    if (isEqual(colId, "locationName")) {
      const data = cloneDeep(node.data?.locationName);
      node.data.locationCd = data?.locCd;
      node.data.locationName = data?.name;
    }

    DataState.setDataState(params);
  }

  const columnDefs = [
    {
      headerName: "locCd", field: "locationCd", colId: "locationCd",
      type: ['nonEditableColumn'],
      // valueFormatter,
      cellClass: 'stringType',
      // comparator: (value1, value2) => objectComparator(value1, value2, "locCd"),
      width: 200, filterType: 'string'
    },
    {
      headerName: "locName", field: "locationName", colId: "locationName",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: dropdownResourceValueFormatter,
      cellEditorParams: {
        valueFormatter: dropdownResourceValueFormatter,
        values: reduxViewState ? reduxViewState.locations : locations
      },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 200,
      filterType: 'string',
    },
    { headerName: "wcCd", field: "wcCd", width: 280, cellClass: 'stringType', filterType: 'string' },
    {
      headerName: "nonOperationCalendar", field: "calendarGroup", colId: "calendarGroup", type: "nonEditableColumn",
      valueFormatter,
      cellRenderer: CalendarGroupRenderer, cellRendererParams: { setSnackInfo, calType: "NOP" },
      comparator: (value1, value2, nodeA, nodeB, isDescending) => objectComparator(value1, value2, "name", isDescending),
      width: 200, filterType: 'string'
    },
    { headerName: "whpd", field: "whpd", type: ["rightAligned"], cellDataType: 'number', valueSetter: checkInputValidation, width: 120, filterType: 'number' },
    { headerName: "overWhpd", field: "overWhpd", type: ["rightAligned"], cellDataType: 'number', valueSetter: checkInputValidation, width: 140, filterType: 'number' },
    { headerName: "wdpw", field: "wdpw", type: ["numberColumn", "rightAligned"], valueSetter: checkInputValidation, width: 120, filterType: 'number' },
    { headerName: "queueHours", field: "queueHours", type: ["rightAligned"], valueSetter: checkInputValidation, width: 120, filterType: 'number' },
    { headerName: "moveHours", field: "moveHours", type: ["rightAligned"], valueSetter: checkInputValidation, width: 120, filterType: 'number' },
    {
      headerName: "frozenRangeSim", field: "frozenRange",
      type: ["numberColumn", "rightAligned"],
      valueSetter: checkFrozenRangeValidation,
      width: 130,
      filterType: 'number',
      filterName: 'frozenRange'
    },
    { headerName: "descTxt", field: "descTxt", width: 350, filterType: 'string' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", valueFormatter: DateUtils.formatDateTime, type: "nonEditableColumn", filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const retrieveLocation = () => {
    locationService.getAll({ pagination: false })
      .then(response => {
        const { results } = response.data;
        const location = [];
        for (const loc of results) {
          location.push({ locCd: loc.locCd, name: loc.name })
          locationMap.current[loc?.locCd] = loc;
        }
        setLocations(location);
        const currentReduxViewState = reduxUtil.getViewState(viewName);
        if (currentReduxViewState) {
          currentReduxViewState.locations = location
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
        }
      })
      .catch(e => {
        debug(e);
      });
  }

  const getRequestParams = () => {
    const params = {};
    const searchFilters = {};

    if (locationSearch) {
      searchFilters.locCd = locationSearch.locCd;
    }

    if (workcenterSearch) {
      searchFilters.wcCd = workcenterSearch.wcCd
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveWorkcenters = () => {
    const params = getRequestParams();

    workcenterService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;

        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setWorkcenters(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const currentReduxViewState = reduxUtil.getViewState(viewName);
        if (currentReduxViewState) {
          currentReduxViewState.locationSearch = locationSearch;
          currentReduxViewState.workcenterSearch = workcenterSearch;
          currentReduxViewState.currentPage = currentPage;
          currentReduxViewState.pageSize = pageSize;
          currentReduxViewState.advancedFilters = advancedFilters;
          reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...currentReduxViewState }));
        }
        DataState.initialize(gridRef.current?.api);
      });
  };

  const onGridReady = params => {
    retrieveLocation();
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const removeWorkcenter = () => {
    const workcenterIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        workcenterIds.push(data.id)
      }
    );

    if (workcenterIds.length > 0) {
      workcenterService.remove(workcenterIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveWorkcenters();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }

  }

  const saveWorkcenter = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    for (const save of allStateData) {
      if (save.locationCd === null) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("location")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      if (save.wdpw === null || save.wdpw === "") {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("workDayPerWeek")}] ${t("MSG_FieldRequired")}` });
        return;
      }

      const workDayPerWeekValue = Number(save.wdpw);

      if (Number.isNaN(workDayPerWeekValue) || !Number.isInteger(workDayPerWeekValue)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: `[${t("workStartDay")}] ${t("MSG_InvalidValue")}` });
        return false;
      }
      save.location = locationMap.current[save.locationCd];
    }

    if (allStateData.length > 0) {
      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

      const dataWithFlg = allStateData.map((row) => {
        const originalData = updatedOrigin[row.rowIndex];
        let activeFlgChanged = false;
        if (originalData) {
          activeFlgChanged = originalData?.activeFlg !== row?.activeFlg;
        }

        return {...row, activeFlgChanged};
      });

      workcenterService.upsert(dataWithFlg)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveWorkcenters();
        });


    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const checkActiveFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
      }
      return false;
    });
  };

  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();

    const hasActiveFlgChanges = checkActiveFlgChanges();

    if (hasActiveFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.workcenter, "deactivate")}` });
    } else {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveWorkcenter();
    } else if (action === "DELETE") {
      removeWorkcenter();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveWorkcenters();
    }
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setWorkcenters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationSearch]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setWorkcenters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workcenterSearch]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setWorkcenters([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveWorkcenters();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    if (!isEmpty(locations) && gridRef?.current) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  const addRow = () => {
    const newRow = {
      id: null,
      locationCd: null,
      locationName: null,
      wcCd: null,
      calendarGroup: null,
      whpd: 8,
      overWhpd: null,
      wdpw: 5,
      queueHours: null,
      moveHours: null,
      frozenRange: null,
      activeFlg: true,
      verNum: 0
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  }

  return (
    <>
      <FilterContainer>
        <Autocomplete
          options={locations}
          getOptionLabel={option => option.name}
          value={locationSearch}
          onChange={(_, newValue) => setLocationSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.locCd === value.locCd}
          size="small"
          sx={{ minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("location")} />
          )}
        />
        <Autocomplete
          options={workcenters}
          getOptionLabel={option => option.wcCd}
          value={workcenterSearch}
          onChange={(_, newValue) => setWorkcenterSearch(newValue)}
          isOptionEqualToValue={(option, value) => option.wcCd === value.wcCd}
          size="small"
          sx={{ pl: 1, minWidth: 190 }}
          renderInput={(params) => (
            <TextField {...params} label={t("workcenter")} />
          )}
        />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={workcenterService} params={getRequestParams()} columnDefs={columnDefs} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.workcenter, 'delete')}` })} />
          <SaveButton onClick={handleSaveClick} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={workcenters}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onCellValueChanged={onCellValueChanged}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={tPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(WorkcenterMaster);
